import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { getClient, getTenantSchedules, type Client, type TenantSchedule } from "@/api/client.service";
import { CrudPage } from "@/components/crud/CrudPage";
import { DataTable } from "@/components/DataTable";
import { Alert, Box, Button, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { Refresh } from "@mui/icons-material";

const outcomeLabel: Record<TenantSchedule["last_outcome"], { label: string; color: string }> = {
  success: { label: "Sucesso", color: "success.main" },
  failed: { label: "Falhou", color: "error.main" },
  timed_out: { label: "Excedeu o tempo", color: "warning.main" },
  "": { label: "Nunca executado", color: "grey.500" },
};

export const ClientDetailPage = () => {
  const { clientId } = useParams<{ clientId: string }>();

  const [client, setClient] = useState<Client>();
  const [schedules, setSchedules] = useState<TenantSchedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    if (!clientId) return;

    setIsLoading(true);
    try {
      const [clientData, schedulesData] = await Promise.all([
        getClient(clientId),
        getTenantSchedules(clientId),
      ]);
      setClient(clientData);
      setSchedules(schedulesData);
      setError(null);
    } catch (e) {
      const status = (e as { status?: number }).status;
      if (status === 502) {
        setError("Agente do cliente inacessível — não foi possível carregar os agendamentos.");
      } else if (status === 403) {
        setError("Você não tem permissão para ver os agendamentos deste cliente.");
      } else {
        setError("Falha ao carregar agendamentos.");
      }
      setSchedules([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [clientId]);

  if (isLoading) return <div>Carregando...</div>;

  const hb = client?.heartbeat;

  return (
    <CrudPage
      title={client?.name ?? "Cliente"}
      subtitle="Status do agente e agendamentos"
      headerActions={
        <Button variant="outlined" startIcon={<Refresh />} onClick={loadData}>
          Atualizar
        </Button>
      }
    >
      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Heartbeat</Typography>
            {hb ? (
              <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                <Box>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Typography sx={{ color: hb.online ? "success.main" : "grey.500" }}>
                    {hb.online ? "Online" : "Offline"}{hb.status ? ` — ${hb.status}` : ""}
                  </Typography>
                </Box>
                {hb.message && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Mensagem</Typography>
                    <Typography>{hb.message}</Typography>
                  </Box>
                )}
                {hb.erp && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">ERP</Typography>
                    <Typography>{hb.erp}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">Versão do agente</Typography>
                  <Typography>{hb.version || "—"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Migração</Typography>
                  <Typography>
                    {hb.migration_state
                      ? `${hb.migration_state}${hb.migration_state === "failed" && hb.migration_error ? `: ${hb.migration_error}` : ""}${hb.migration_version ? ` (v${hb.migration_version})` : ""}`
                      : "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Visto por último</Typography>
                  <Typography>{new Date(hb.last_seen_at).toLocaleString()}</Typography>
                </Box>
              </Stack>
            ) : (
              <Typography color="text.secondary">Nenhum heartbeat recebido deste agente ainda.</Typography>
            )}
          </Paper>

          <Typography variant="subtitle1" gutterBottom>Agendamentos ativos no agente</Typography>
          <DataTable<TenantSchedule>
            data={schedules}
            columns={[
              { key: "name", label: "Nome" },
              { key: "cron", label: "Cron" },
              {
                key: "command",
                label: "Comando",
                render: (s) => (
                  <Tooltip title={s.command}>
                    <Box component="span" sx={{ display: "inline-block", maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "bottom" }}>
                      {s.command}
                    </Box>
                  </Tooltip>
                ),
              },
              { key: "timeout_seconds", label: "Timeout (s)" },
              {
                key: "next_run",
                label: "Próxima execução",
                render: (s) => <>{new Date(s.next_run).toLocaleString()}</>,
              },
              {
                key: "last_run",
                label: "Última execução",
                render: (s) => {
                  const t = new Date(s.last_run).getTime();
                  return <>{t > 0 ? new Date(s.last_run).toLocaleString() : "—"}</>;
                },
              },
              {
                key: "last_outcome",
                label: "Resultado",
                render: (s) => {
                  const o = outcomeLabel[s.last_outcome];
                  return <Box component="span" sx={{ color: o.color, fontSize: "0.8rem" }}>{o.label}</Box>;
                },
              },
            ]}
            total={schedules.length}
            page={0}
            rowsPerPage={Math.max(schedules.length, 1)}
            onPageChange={() => {}}
            onRowsPerPageChange={() => {}}
          />
        </>
      )}
    </CrudPage>
  );
};
